import { Directive, Input, Output, EventEmitter, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Firestore, doc, docData, setDoc, collection, DocumentReference } from '@angular/fire/firestore';
import { UntypedFormGroup } from '@angular/forms';
import { take, tap, debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

export type FireFormState = 'loading' | 'synced' | 'modified' | 'error';

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[fireForm]'
})
export class FireFormDirective implements OnInit, OnDestroy {

  // Inputs
  @Input({ required: true }) path!: string;
  @Input({ required: true }) formGroup!: UntypedFormGroup;

  // Internal state
  private _state: FireFormState = 'loading';

  // Outputs
  @Output() stateChange = new EventEmitter<FireFormState>();
  @Output() formError = new EventEmitter<string>();
  @Output() savedChange = new EventEmitter<unknown>();

  // Firestore Document
  private docRef?: DocumentReference;

  // Subscriptions
  private formSub?: Subscription;

  constructor(private firestore: Firestore) { }

  ngOnInit() {
    this.preloadData();
    this.autoSave();
  }


  // Loads initial form data from Firestore
  preloadData() {
    this.state = 'loading';
    this.docRef = this.getDocRef(this.path);
    docData(this.docRef)
      .pipe(
        tap(doc => {
          if (doc) {
            this.formGroup.patchValue(doc);
            this.formGroup.markAsPristine();
            this.state = 'synced';
          } else {
            this.state = 'synced';
          }
        }),
        take(1)
      )
      .subscribe();
  }


  // Autosaves form changes
  autoSave() {
    this.formSub = this.formGroup.valueChanges
      .pipe(
        tap(() => {
          this.state = (this._state === 'loading') ? 'loading' : 'modified';
        }),
        debounceTime(2000),
        tap(() => {
          if (this.formGroup.valid && this._state === 'modified') {
            this.setDoc();
          }
        })
      )
      .subscribe();
  }

  // Intercept form submissions to perform the document write
  @HostListener('ngSubmit') onSubmit() {
    this.setDoc();
  }


  // Determines if path is a collection or document
  getDocRef(path: string): DocumentReference {
    if (path.split('/').length % 2) {
      return doc(collection(this.firestore, path));
    } else {
      return doc(this.firestore, path);
    }
  }


  // Writes changes to Firestore
  async setDoc() {
    if (!this.docRef) {
      return;
    }
    try {
      await setDoc(this.docRef, this.formGroup.value, { merge: true });
      this.formGroup.markAsPristine();
      this.savedChange.emit(this.formGroup.value);
      this.state = 'synced';
    } catch (err) {
      console.log(err);
      this.formError.emit(err instanceof Error ? err.message : String(err));
      this.state = 'error';
    }
  }

  // Setter for state changes
  set state(val: FireFormState) {
    this._state = val;
    this.stateChange.emit(val);
  }

  ngOnDestroy() {
    this.formSub?.unsubscribe();
  }
}